import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useSpacePrices } from '@cocoonspace/shared/hooks/cocoon-api'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { fr } from 'date-fns/locale'
import { useTranslation } from 'next-i18next'
import { useCallback, useMemo } from 'react'
import { Calendar, type CalendarProps } from '~/components/ui/calendar'
import CalendarDayWithPrice from '~/domains/booking/components/atoms/calendar-day-with-price'

const locales = {
	fr,
}

export const styles: CalendarProps['classNames'] = {
	table: 'mx-0 w-full mt-2',
	button:
		'!text-sm rounded-full h-[45px] w-[45px] flex items-center justify-center cursor-pointer',
	head_cell: 'text-sm h-[30px] !w-[30px] font-normal opacity-50 p-1',
	cell: 'p-[1px] h-[50px] w-[50px]',
	month: 'px-4',
}

export const DetailsSidebarStepMonth = ({
	space,
	selectedDay,
	maxDate,
	spaceId,
	showPrices = true,
	onDaySelect,
}: {
	space?: Space
	selectedDay?: string
	maxDate: string
	spaceId: string
	showPrices?: boolean
	onDaySelect: (day: string) => void
}) => {
	const { t, i18n } = useTranslation()

	const { data: calendarPrices } = useSpacePrices(spaceId, {
		to: maxDate,
	})

	const handleDayChange = useCallback(
		(day?: Date) => {
			if (!day) {
				return onDaySelect(
					selectedDay ?? dateTimeUtils().customFormat('dateTech'),
				)
			}

			onDaySelect(dateTimeUtils(day).customFormat('dateTech'))
		},
		[onDaySelect, selectedDay],
	)

	const maxDateNative = useMemo(
		() => dateTimeUtils(maxDate).toDate(),
		[maxDate],
	)

	const selectedDayNative = useMemo(
		() => dateTimeUtils(selectedDay).toDate(),
		[selectedDay],
	)

	const disabled = calendarPrices
		?.filter(({ closed }) => closed)
		.map(({ day }) => dateTimeUtils(day).tz(space?.timezone).toDate())

	return (
		<div className='max-h-[512px] w-full overflow-x-hidden overflow-y-scroll overscroll-contain pb-4'>
			<div className='sticky top-0 z-10 flex h-[60px] flex-row items-center justify-center bg-white text-center font-bold text-lg'>
				{t('booking:stepper.monthStep.title')}
			</div>

			<Calendar
				locale={locales[(i18n.language as 'fr') || 'fr']}
				disableNavigation
				classNames={styles}
				numberOfMonths={13}
				fromDate={dateTimeUtils().toDate()}
				toDate={maxDateNative}
				selected={selectedDayNative}
				mode='single'
				disabled={disabled}
				components={
					showPrices
						? {
								DayContent: ({ date }) => (
									<CalendarDayWithPrice
										date={date}
										calendarPrices={calendarPrices || []}
									/>
								),
							}
						: undefined
				}
				onSelect={handleDayChange}
			/>
		</div>
	)
}
